import { Theme } from '@mui/material';
import dayjs from 'dayjs';
import { TZDate } from 'react-day-picker';

import { ComponentStyles } from '../../../types';
import { tokens } from '../../../theme/generated/tokens';
import { dayPickerStyles } from '../dayPickerStyles';

type IndexedMonth = {
  date: TZDate;
  index: number;
};

export const getScrollableMonths = (
  startDate: TZDate,
  endDate: TZDate,
  timezone: string
): IndexedMonth[] => {
  const result = [];

  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth();

  for (let year = startYear; year <= endYear; year++) {
    const monthStart = year === startYear ? startMonth : 0;
    const monthEnd = year === endYear ? endMonth : 11;

    for (let month = monthStart; month <= monthEnd; month++) {
      const monthDate = new TZDate(year, month, 1, timezone);
      result.push({
        date: monthDate,
        index: year * 12 + month,
      });
    }
  }

  return result;
};

export const blueTheme = (theme: Theme): ComponentStyles =>
  dayPickerStyles({
    typography: theme.typography,
    text: tokens.theme.fg.structural.secondary,
    textDisabled: tokens.theme.disabled.fg,
    label: tokens.theme.fg.structural.primary,
    accentLight: tokens.theme.bg.selection.primarySubtleHover,
    accentExtraLight: tokens.theme.bg.selection.primarySubtle,
    accentContrast: tokens.theme.bg.action.surfaceHover,
    accent: tokens.theme.border.selection.primary,
    accentDark: tokens.theme.bg.action.primary,
    accentExtraDark: tokens.theme.bg.selection.primaryActive,
    background: tokens.theme.bg.action.surfaceHover,
    backgroundTrail: tokens.theme.bg.action.surfaceHover,
    activeBackground: tokens.theme.bg.action.primaryHover,
  });

export const greenTheme = (theme: Theme): ComponentStyles =>
  dayPickerStyles({
    typography: theme.typography,
    text: tokens.theme.fg.structural.secondary,
    textDisabled: tokens.theme.disabled.fg,
    label: tokens.theme.fg.structural.primary,
    accentLight: tokens.theme.bg.selection.secondarySubtleHover,
    accentExtraLight: tokens.theme.bg.selection.secondarySubtle,
    accentContrast: tokens.theme.bg.action.surfaceHover,
    accent: tokens.theme.border.selection.secondary,
    accentDark: tokens.theme.bg.action.secondary,
    accentExtraDark: tokens.theme.bg.selection.secondaryActive,
    background: tokens.theme.bg.action.surfaceHover,
    backgroundTrail: tokens.theme.bg.action.surfaceHover,
    activeBackground: tokens.theme.bg.action.secondaryHover,
  });

export const getWeekDays = (): string[] => {
  return [...Array(7)].map((_, index) =>
    dayjs()
      .day(index + 1)
      .format('ddd')
  );
};

export const getMonthIndex = (
  date: TZDate,
  allMonths: IndexedMonth[]
): number => {
  const initialMonthNumber = date.getFullYear() * 12 + date.getMonth();

  return allMonths.findIndex((m) => m.index === initialMonthNumber);
};

export const validateDateInput = (
  dateString: string,
  allowTime: boolean
): boolean => {
  const datePattern = /^\d{4}\/\d{2}\/\d{2}$/;
  const dateTimePattern = /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/;

  return allowTime
    ? dateTimePattern.test(dateString)
    : datePattern.test(dateString);
};
